import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { Icon } from '@pella/sash-framework';
import { Image } from '../../';

import './index.css';

const Arrow = ({ className, onClick, type }) => (
	<button
		onClick={onClick}
		aria-label={type === 'next' ? 'Next' : 'Previous'}
		className={`z-2 absolute top-1/2 flex h-28 w-7 -translate-y-1/2 items-center justify-center border border-neutral-100 bg-white text-black shadow-[0.5px_3px_6px_rgba(26,26,26,0.1)] md:h-10 md:w-10 ${type === 'next' ? '-right-3 md:-right-5' : '-left-3 md:-left-5'} ${className || ''}`}
	>
		<Icon name={type === 'next' ? 'arrow_right' : 'arrow_left'} />
	</button>
);

Arrow.propTypes = {
	className: PropTypes.string,
	onClick: PropTypes.func,
	type: PropTypes.string,
};

const ThumbArrow = ({ className, onClick, type }) => (
	<button
		onClick={onClick}
		aria-label={type === 'next' ? 'Next' : 'Previous'}
		className={`z-2 -indent-full absolute top-0 h-full w-11 from-white ${type === 'next' ? '-right-6 bg-gradient-to-l md:-right-px' : '-left-6 bg-gradient-to-r md:left-0'} ${className || ''}`}
	>
		{type === 'next' ? 'Next' : 'Previous'}
	</button>
);

ThumbArrow.propTypes = {
	className: PropTypes.string,
	onClick: PropTypes.func,
	type: PropTypes.string,
};

const SliderWithThumbnails = ({ images, className, onSlideChange, mainSettings = {}, thumbSettings = {}, scrollTo }) => {
	const mainSliderRef = useRef(null);
	const thumbSliderRef = useRef(null);
	const [sliderNav, setSliderNav] = useState({ main: null, thumb: null });
	const [activeSlide, setActiveSlide] = useState(0);

	useEffect(() => setSliderNav({ main: mainSliderRef.current, thumb: thumbSliderRef.current }), []);

	useEffect(() => {
		if (scrollTo) {
			mainSliderRef.current.slickGoTo(scrollTo);
			thumbSliderRef.current.slickGoTo(scrollTo);
		}
	}, [scrollTo]);

	if (!images?.length) return null;

	const onBeforeSlideChange = (current, next) => {
		setActiveSlide(next);
		if (typeof onSlideChange === 'function') onSlideChange(next);
	};

	return (
		<div className={`sliderWithThumbnails relative ${className || ''}`}>
			<Slider
				asNavFor={sliderNav.thumb}
				ref={(slider) => (mainSliderRef.current = slider)}
				infinite={false}
				centerMode={false}
				lazyLoad={true}
				beforeChange={onBeforeSlideChange}
				nextArrow={<Arrow type='next' />}
				prevArrow={<Arrow type='prev' />}
				responsive={[
					{
						breakpoint: 767,
						settings: { arrows: true },
					},
				]}
				{...mainSettings}
			>
				{images.map((image, index) => (
					<div
						key={index}
						className='text-center'
					>
						<Image data={image} />
					</div>
				))}
			</Slider>

			<p className='mb-4 mt-5 text-center text-xs text-neutral-600 md:mb-5 md:text-sm'>
				Slide {activeSlide + 1} / {images.length}
			</p>

			<Slider
				asNavFor={sliderNav.main}
				ref={(slider) => (thumbSliderRef.current = slider)}
				slidesToShow={6}
				infinite={false}
				centerMode={false}
				lazyLoad={true}
				focusOnSelect={true}
				className='relative [&>div>div]:mx-auto md:[&>div>div]:flex [&>div]:-mx-1 max-md:[&>div]:!overflow-visible'
				nextArrow={<ThumbArrow type='next' />}
				prevArrow={<ThumbArrow type='prev' />}
				responsive={[
					{
						breakpoint: 1279,
						settings: { slidesToShow: 5 },
					},
					{
						breakpoint: 767,
						settings: { slidesToShow: 5.5 },
					},
					{
						breakpoint: 549,
						settings: { slidesToShow: 4.5 },
					},
				]}
				{...thumbSettings}
			>
				{images.map((image, index) => (
					<div
						key={index}
						className='px-1'
					>
						<Image
							data={image}
							className={`aspect-square cursor-pointer border transition-opacity xl:aspect-[3/2] ${activeSlide === index ? 'border-primary opacity-100 shadow-[0_2px_5px_rgba(26,26,26,0.3)]' : 'border-transparent opacity-50 hover:opacity-100 focus:opacity-100'}`}
						/>
					</div>
				))}
			</Slider>
		</div>
	);
};

SliderWithThumbnails.propTypes = {
	images: PropTypes.array.isRequired,
	className: PropTypes.string,
	onSlideChange: PropTypes.func,
	mainSettings: PropTypes.object,
	thumbSettings: PropTypes.object,
	scrollTo: PropTypes.number,
};

export default SliderWithThumbnails;
